// @ts-nocheck
import { USER_ROLES } from "../../staticData/userRoles";
import { store } from "../../store/store";
import { logger } from "./logger";

/**
 * @typedef {"admin" | "coordinator" | "client" | "superAdmin" | "contractor"} Roles
 */

/**
 * Check if the user HAS any of the provided roles APPROVED.
 *
 * Post-unification: reads from `auth.approvedRoles` (source of truth for
 * permissions). Falls back to `auth.userRole` if approvedRoles is not yet
 * populated (legacy token).
 *
 * Use for PERMISSION CHECKS (e.g. "can the user edit?" — any approved
 * role satisfies it). For UI/layout/sidebar checks use `isActiveRole`.
 *
 * Case-insensitive: tolerates 'Admin'/'admin', 'Super Admin'/'superAdmin'.
 *
 * @param  {...Roles} roles
 * @returns {boolean}
 */
export const isUserRole = (...roles) => {
  try {
    const auth = store.getState().auth || {};
    const approved = Array.isArray(auth.approvedRoles)
      ? auth.approvedRoles
      : [];
    // Fallback: legacy token without approvedRoles → use userRole
    const haveRoles =
      approved.length > 0 ? approved : auth.userRole ? [auth.userRole] : [];
    if (haveRoles.length === 0) {
      logger.log("User has no approved roles");
      return false;
    }
    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
    const haveNorm = haveRoles.map(norm);
    return roles.some((r) => haveNorm.includes(norm(r)));
  } catch (_) {
    return false;
  }
};

/**
 * Check if the user's ACTIVE role (current UI) matches one of the provided.
 *
 * Use for UI rendering: layout/sidebar/redirect logic. Different from
 * `isUserRole` (which is an OR-aggregate over all approved roles). `isActiveRole`
 * checks only the role that is visually active (set by the role
 * switcher).
 *
 * @param  {...Roles} roles
 * @returns {boolean}
 */
export const isActiveRole = (...roles) => {
  try {
    const auth = store.getState().auth || {};
    const approved = Array.isArray(auth.approvedRoles) ? auth.approvedRoles : [];
    // Fallback: if activeRole is not set OR has no layout of its own
    // (e.g. contact/subCoordinator), use the first approvedRole that has
    // a supported layout. Avoids a black screen when the role switcher
    // leaves the state ambiguous.
    const ROLES_WITH_LAYOUT = [
      "superAdmin",
      "admin",
      "client",
      "coordinator",
      "contractor",
      "freelanceEc",
      "marketing",
    ];
    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
    const layoutNorm = ROLES_WITH_LAYOUT.map(norm);
    let active = auth.activeRole || auth.userRole;
    if (!active || !layoutNorm.includes(norm(active))) {
      active = approved.find((r) => layoutNorm.includes(norm(r))) || active;
    }
    if (!active) return false;
    const activeNorm = norm(active);
    return roles.some((r) => norm(r) === activeNorm);
  } catch (_) {
    return false;
  }
};

/**
 * Function to get PO or TimeSheet or PO/TimeSheet for user roles
 * @param {boolean} isFull
 * @returns {string}
 */
export const getPOTimeSheetName = (isFull) => {
  try {
    const userRole = store.getState().auth.userRole;
    if (!userRole) {
      throw new Error(".");
    } else {
      if (userRole === USER_ROLES.CONTRACTOR) {
        const contractorType =
          store.getState().auth?.contractorDetails?.contractorType;
        if (contractorType == "w2") {
          return "TimeSheet";
        } else {
          if (isFull) return "Purchase Order/TimeSheet";
          else return "PO/TimeSheet";
        }
      } else {
        throw new Error(".");
      }
    }
  } catch (_) {
    if (isFull) {
      return "Purchase Order/TimeSheet";
    } else {
      return "PO/TimeSheet";
    }
  }
};